/**
 * Retailer React Query Hooks — data source for the grocery-ordering
 * retailer picker ("Shop this list").
 */

import { useMutation, useQuery, useQueryClient, type QueryFilters } from "@tanstack/react-query";
import type { RetailerLineKey, RetailerLineSelection, RetailerSummary } from "@batch-and-bowl/shared";
import type { GroceryList } from "../api/types";
import * as retailerApi from "../api/retailers";
import { RETAILERS_QUERY_KEY, GROCERY_LISTS_QUERY_KEY } from "./query-keys";
import { retryKrogerSearch } from "../kroger-search-errors";
import { krogerProductSearchQuerySchema } from "@batch-and-bowl/validators";

export { RETAILERS_QUERY_KEY };

function isGroceryListCache(value: unknown): value is GroceryList {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<GroceryList>;
  return typeof candidate.id === "string" && Array.isArray(candidate.items);
}

/**
 * Remove read-time prices before a store identity changes. The list items and
 * their checkoff state remain available while the next GET obtains quotes for
 * the new store.
 */
async function refreshGroceryPricing(
  queryClient: ReturnType<typeof useQueryClient>,
  groceryListId?: string,
) {
  const filters: QueryFilters = {
    queryKey: [GROCERY_LISTS_QUERY_KEY],
    ...(groceryListId ? { predicate: (query) =>
      isGroceryListCache(query.state.data) && query.state.data.id === groceryListId } : {}),
  };
  await queryClient.cancelQueries(filters);
  queryClient.setQueriesData<GroceryList>(
    filters,
    (old) => {
      if (!isGroceryListCache(old)) return old;
      const withoutStalePricing = { ...old };
      delete withoutStalePricing.costTotals;
      if (old.items.some((item) => item.product)) {
        withoutStalePricing.items = old.items.map((item) => ({ ...item, product: null }));
      }
      return withoutStalePricing;
    },
  );
  await queryClient.invalidateQueries(filters);
}

function invalidateGroceryPricingForList(
  queryClient: ReturnType<typeof useQueryClient>,
  groceryListId: string,
) {
  return refreshGroceryPricing(queryClient, groceryListId);
}

/**
 * The retailers configured on this deployment, with capability descriptors
 * and link status. Which retailers exist changes only with a server deploy,
 * and link status changes only through an explicit connect/disconnect flow
 * (which invalidates this key) — so no polling.
 */
export function useRetailers() {
  return useQuery({
    queryKey: [RETAILERS_QUERY_KEY],
    queryFn: async () => {
      const response = await retailerApi.getRetailers();
      return response.data;
    },
  });
}

const ZIP_PATTERN = /^\d{5}$/;

/** Nearby Kroger stores; fetches only once the zip is a complete 5 digits. */
export function useKrogerStores(zip: string) {
  return useQuery({
    queryKey: [RETAILERS_QUERY_KEY, "kroger-stores", zip],
    queryFn: async () => {
      const response = await retailerApi.getKrogerStores(zip);
      return response.data;
    },
    enabled: ZIP_PATTERN.test(zip),
  });
}

/** Debounced by the caller; disabled until the catalogue dialog has a term. */
export function useKrogerProductSearch(term: string, enabled = true, storeLocationId?: string | null) {
  const query = krogerProductSearchQuerySchema.safeParse({ q: term });
  return useQuery({
    queryKey: [RETAILERS_QUERY_KEY, "kroger-products", storeLocationId, term],
    queryFn: async () => {
      const response = await retailerApi.searchKrogerProducts(term);
      return response.data;
    },
    enabled: enabled && Boolean(storeLocationId) && query.success,
    staleTime: 30_000,
    retry: retryKrogerSearch,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Persist the chosen Kroger store; refreshes the retailer list (storeLocationId). */
export function useSetKrogerStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (storeLocationId: string) =>
      retailerApi.setKrogerStore(storeLocationId),
    onSuccess: async (selection) => {
      await queryClient.cancelQueries({ queryKey: [RETAILERS_QUERY_KEY] });
      queryClient.setQueryData<RetailerSummary[]>([RETAILERS_QUERY_KEY], (old) =>
        old?.map((retailer) => retailer.id === "kroger" ? { ...retailer, ...selection } : retailer),
      );
      await refreshGroceryPricing(queryClient);
      await queryClient.invalidateQueries({ queryKey: [RETAILERS_QUERY_KEY] });
    },
  });
}

/** Match unchecked list items to retailer products for the review step. */
export function useMatchRetailerItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      retailerId,
      groceryListId,
      forceLineKeys,
    }: {
      retailerId: string;
      groceryListId: string;
      forceLineKeys?: RetailerLineKey[];
    }) =>
      forceLineKeys
        ? retailerApi.matchRetailerItems(retailerId, groceryListId, forceLineKeys)
        : retailerApi.matchRetailerItems(retailerId, groceryListId),
    onSuccess: async (_data, variables) => {
      await invalidateGroceryPricingForList(queryClient, variables.groceryListId);
    },
  });
}

/**
 * Persist the user's chosen product/quantity/exclusion for one or more
 * lines (#444, closes #416). MatchReview fires this once per interaction,
 * keyed by the same `ingredientId`+`unit` identity its local selection
 * state uses -- never array index.
 */
export function useUpdateRetailerSelections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      retailerId,
      groceryListId,
      selections,
    }: {
      retailerId: string;
      groceryListId: string;
      selections: RetailerLineSelection[];
    }) =>
      retailerApi.updateRetailerSelections(retailerId, groceryListId, selections),
    onSuccess: async (_data, variables) => {
      await invalidateGroceryPricingForList(queryClient, variables.groceryListId);
    },
  });
}

/**
 * Push confirmed selections into the user's real Kroger cart. On success,
 * invalidates the grocery list query -- a successful push may have captured
 * live prices server-side (Track D, D1, issue #382), and the read path is
 * the only place those show up -- and this list's cart-status query
 * (issue #441), whose `cartCount` ("Added so far") may have just changed.
 */
export function usePushKrogerCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      groceryListId,
      selections,
    }: {
      groceryListId: string;
      selections: retailerApi.KrogerCartSelection[];
    }) => retailerApi.pushKrogerCart(groceryListId, selections),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [RETAILERS_QUERY_KEY, "cart-status", variables.groceryListId],
      });
    },
  });
}

/**
 * Read-only "Added so far" count for a grocery list's Kroger cart ledger
 * (issue #441) -- lets the Groceries page show cumulative cart progress
 * (across pushes, across tabs) without requiring a fresh push. Fetches only
 * once a real grocery list id exists.
 */
export function useKrogerCartStatus(groceryListId: string | undefined) {
  return useQuery({
    queryKey: [RETAILERS_QUERY_KEY, "cart-status", groceryListId],
    queryFn: () => retailerApi.getKrogerCartStatus(groceryListId!),
    enabled: Boolean(groceryListId),
  });
}

/** Unlink the user's Kroger account; refreshes the retailer list. */
export function useDisconnectKroger() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retailerApi.disconnectKroger(),
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey: [RETAILERS_QUERY_KEY] });
      queryClient.setQueryData<RetailerSummary[]>([RETAILERS_QUERY_KEY], (old) =>
        old?.map((retailer) => retailer.id === "kroger" ? { ...retailer, linked: false, storeLocationId: undefined, storeName: undefined, storeChain: undefined } : retailer),
      );
      await refreshGroceryPricing(queryClient);
      await queryClient.invalidateQueries({ queryKey: [RETAILERS_QUERY_KEY] });
    },
  });
}
